import { useSelector } from "react-redux";
import { RootReduxState } from "~/modules/app/components/ReduxProvider/types";
import { useUser } from "~/modules/user/hooks/useUser";
import { removeFeatureFlaggedResults } from "../../util/removeFeatureFlaggedResults";
import { useFilteredSearchResults } from "../../util/useFilteredSearchResults";
import { SearchResultIcon } from "../../components/SearchWindow/SearchIcon";
import { useSearchParams } from "next/navigation";
import { Link } from "~/components/Link";
import { SearchEvents } from "~/modules/analytics/events/search";
import { PageHeader } from "~/components/PageHeader";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { useEffect } from "react";
import { SearchInput } from "~/components/SearchInput";

// // // //

/**
 * Search Page component
 * Displays a full-page search interface with results
 */
export function SearchPage() {
    const searchParams = useSearchParams();
    const { user } = useUser();
    const { groups, loading } = useSelector(
        (state: RootReduxState) => state.search,
    );

    // Get the filtered groups based on the user's feature flags
    const filteredGroups = removeFeatureFlaggedResults({
        groups,
        featureFlagState: user.features,
    });

    // Read the query from URL parameters
    const urlQuery = searchParams?.get("query") ?? "";

    const { query, setQuery, searchGroupings, filteredItems } =
        useFilteredSearchResults({
            groups: filteredGroups,
            initialQuery: urlQuery,
        });

    // Sync the search query FROM the URL when it changes (e.g., on navigation or page load)
    useEffect(() => {
        // Only update if the URL query is different from the current search query
        if (urlQuery !== query) {
            setQuery(urlQuery);
        }
    }, [urlQuery]);

    return (
        <div className="flex flex-col min-h-[calc(100vh-16rem)]">
            <PageHeader
                title="Search"
                subtitle="Search and browse all available pages and features"
            />

            {/* Search Input */}
            <div className="mb-6 max-w-5xl">
                <SearchInput
                    value={query}
                    onChange={setQuery}
                    placeholder="Search..."
                    size="md"
                />
            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center py-16 max-w-5xl">
                    <LoadingSpinner size={10} />
                </div>
            )}

            {/* Content wrapper to ensure full height */}
            <div className="flex-1">
                {/* Empty State */}
                {!loading && query !== "" && filteredItems.length === 0 && (
                    <div className="max-w-5xl">
                        <div className="bg-card rounded-lg border shadow-xs p-8 text-center">
                            <p className="text-foreground text-base font-medium mb-2">
                                {`No results found for "${query}"`}
                            </p>
                            <p className="text-muted-foreground text-sm">
                                Try adjusting your search or browse all
                                available items below
                            </p>
                        </div>
                    </div>
                )}

                {/* Search Results */}
                {!loading && filteredItems.length > 0 && query !== "" && (
                    <div className="space-y-6 max-w-5xl">
                        {Object.entries(searchGroupings).map(
                            ([category, items]) => (
                                <div key={category}>
                                    <h2 className="text-lg font-semibold text-foreground mb-3 px-2">
                                        {category}
                                    </h2>
                                    <ul className="space-y-1 bg-card rounded-lg border shadow-xs overflow-hidden">
                                        {items.map((item) => (
                                            <li key={item.id}>
                                                <Link
                                                    href={item.href}
                                                    analyticsLabel={
                                                        SearchEvents.SearchPageSelectResult
                                                    }
                                                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors cursor-pointer border-b last:border-b-0"
                                                >
                                                    <SearchResultIcon
                                                        src={item.content.icon}
                                                        wrapperClassName={
                                                            item.content
                                                                .iconWrapperClassName
                                                        }
                                                        className={
                                                            item.content
                                                                .iconClassName
                                                        }
                                                        alt={`${item.content.label} Search Result Icon`}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-base font-medium text-foreground">
                                                            {item.content.label}
                                                        </p>
                                                        {item.content
                                                            .subtitle && (
                                                            <p className="text-sm text-muted-foreground truncate">
                                                                {
                                                                    item.content
                                                                        .subtitle
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                        )}
                    </div>
                )}

                {/* Initial State - Show all results when query is empty */}
                {!loading && query === "" && filteredItems.length > 0 && (
                    <div className="space-y-6 max-w-5xl">
                        <p className="text-muted-foreground text-sm px-2">
                            Start typing to search, or browse all available
                            items below.
                        </p>
                        {Object.entries(searchGroupings).map(
                            ([category, items]) => (
                                <div key={category}>
                                    <h2 className="text-lg font-semibold text-foreground mb-3 px-2">
                                        {category}
                                    </h2>
                                    <ul className="space-y-1 bg-card rounded-lg border shadow-xs overflow-hidden">
                                        {items.map((item) => (
                                            <li key={item.id}>
                                                <Link
                                                    href={item.href}
                                                    analyticsLabel={
                                                        SearchEvents.SearchPageSelectResult
                                                    }
                                                    analyticsEventProperties={{
                                                        label: item.content
                                                            .label,
                                                        resultId: item.id,
                                                    }}
                                                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors cursor-pointer border-b last:border-b-0"
                                                >
                                                    <SearchResultIcon
                                                        src={item.content.icon}
                                                        className={
                                                            item.content
                                                                .iconClassName
                                                        }
                                                        wrapperClassName={
                                                            item.content
                                                                .iconWrapperClassName
                                                        }
                                                        alt={`${item.content.label} Search Result Icon`}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-base font-medium text-foreground">
                                                            {item.content.label}
                                                        </p>
                                                        {item.content
                                                            .subtitle && (
                                                            <p className="text-sm text-muted-foreground truncate">
                                                                {
                                                                    item.content
                                                                        .subtitle
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
